/**
 * VariantEditor — flat key/value variant rows (EPIC-006 Phase 7, Task 7.2;
 * story 006-006 front end).
 *
 * Each row is a `variant_attribute_key` + `variant_attribute_value` pair (both
 * required, non-empty — Zod-mirrored). There is NO per-variant price/stock
 * (frozen schema). Variants persist against an EXISTING product, so the editor
 * disables the add control until the product has been saved once (a `productId`
 * exists); the empty hint explains why.
 *
 * Add → `POST /sellers/me/products/{pid}/variants`; remove →
 * `DELETE .../variants/{vid}`. On error a toast surfaces and the optimistic row
 * is rolled back by re-reading the server list.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Icon } from "@garden/ui";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
} from "@/features/seller-dashboard/api";
import {
  useCreateVariant,
  useDeleteVariant,
  useSellerVariants,
} from "@/features/seller-dashboard/hooks/useSellerProducts";
import type { VariantPort } from "@/features/product-editor/port";

export interface VariantEditorProps {
  /** Null when the product has not been saved yet (variants need an id). */
  productId: string | null;
  /** Injected variant CRUD surface. Optional for backward compatibility —
   * omitted, this falls back to the seller hooks directly (today's exact
   * behavior). The shared `ProductEditor` always supplies this explicitly;
   * only the (soon to be replaced) legacy seller page relies on the
   * fallback. */
  variants?: VariantPort;
}

const SELLER_VARIANT_PORT: VariantPort = {
  useList: useSellerVariants,
  useCreate: useCreateVariant,
  useDelete: useDeleteVariant,
};

export function VariantEditor({ productId, variants }: VariantEditorProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const port = variants ?? SELLER_VARIANT_PORT;
  const { data } = port.useList(productId);
  const createVariant = port.useCreate();
  const deleteVariant = port.useDelete();

  const [draftKey, setDraftKey] = useState("");
  const [draftValue, setDraftValue] = useState("");
  const [rowError, setRowError] = useState<string | null>(null);

  // Named `variantRows` (not `variants`) to avoid colliding with the
  // `variants` prop above (the injected `VariantPort`).
  const variantRows = data?.variants ?? [];
  const canAdd = productId !== null;

  const handleAdd = (): void => {
    const key = draftKey.trim();
    const value = draftValue.trim();
    if (key === "" || value === "") {
      setRowError(t("variants.bothRequired"));
      return;
    }
    if (productId === null) return;
    setRowError(null);
    createVariant.mutate(
      { productId, key, value },
      {
        onSuccess: () => {
          setDraftKey("");
          setDraftValue("");
        },
        onError: () => toast.error(t("variants.addFailed")),
      },
    );
  };

  const handleRemove = (variantId: string): void => {
    if (productId === null) return;
    deleteVariant.mutate(
      { productId, variantId },
      { onError: () => toast.error(t("variants.removeFailed")) },
    );
  };

  return (
    <div data-testid="variant-editor" className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">{t("variants.hint")}</p>
      {!canAdd ? (
        <p
          data-testid="variants-save-first"
          className="rounded-[var(--g-radius)] bg-secondary px-3 py-2 text-xs text-secondary-foreground"
        >
          {t("variants.saveFirst")}
        </p>
      ) : null}

      {variantRows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="variants-empty">
          {t("variants.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="variant-rows">
          {variantRows.map((v) => (
            <li
              key={v.id}
              className="flex items-center gap-2 rounded-[var(--g-radius)] border border-border bg-card px-3 py-2"
              data-testid="variant-row"
            >
              <span className="text-sm font-medium text-foreground">
                {v.variant_attribute_key}
              </span>
              <span className="text-muted-foreground">—</span>
              <span className="flex-1 text-sm text-foreground">
                {v.variant_attribute_value}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(v.id)}
                aria-label={t("variants.remove")}
                data-testid={`variant-remove-${v.id}`}
                className="inline-flex size-11 items-center justify-center rounded-[var(--g-radius)] text-[var(--g-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon name="x" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor="variant-key" className="text-xs text-muted-foreground">
            {t("variants.key")}
          </label>
          <input
            id="variant-key"
            data-testid="variant-key-input"
            value={draftKey}
            onChange={(e) => setDraftKey(e.target.value)}
            placeholder={t("variants.keyPlaceholder")}
            className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor="variant-value" className="text-xs text-muted-foreground">
            {t("variants.value")}
          </label>
          <input
            id="variant-value"
            data-testid="variant-value-input"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder={t("variants.valuePlaceholder")}
            className="min-h-[44px] rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground outline-none"
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd || createVariant.isPending}
          data-testid="variant-add"
          className="inline-flex min-h-[44px] items-center rounded-[var(--g-radius)] bg-primary px-4 text-sm font-semibold text-[var(--g-on-primary)] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t("variants.add")}
        </button>
      </div>
      {rowError ? (
        <p role="alert" className="text-xs text-[var(--g-error)]">
          {rowError}
        </p>
      ) : null}
    </div>
  );
}

export default VariantEditor;
